import {
  selectButtonDestination,
  selectButtonTitle,
  selectIsPurchasing, selectIsUpdating,
  selectPurchaseOrder
} from "../../redux/checkout/checkoutSlice";
import React from "react";
import {useAppSelector} from "../../app/hooks";
import {ImageUtils} from "../../utils/imageUtils";
import Skeleton from "react-loading-skeleton";
import {Image} from "../images/Image";
import {Body, Heading3, LocalizedTextLabel, Paragraph} from "../text";
import {useTranslation} from "next-i18next";
import {Button} from "../buttons/Button";
import {CurrencyUtils} from "../../utils/currencyUtils";
import {Container} from "../containers/Container";

interface CheckoutPaymentSummaryViewProps {
  isLoading: boolean
  onButtonClick: () => void
}

export const CheckoutPaymentSummaryView: React.FC<CheckoutPaymentSummaryViewProps> = ({ isLoading, onButtonClick }) => {
  const purchaseOrder = useAppSelector(selectPurchaseOrder)
  const buttonTitle = useAppSelector(selectButtonTitle)
  const isPurchasing = useAppSelector(selectIsPurchasing)
  const isUpdating = useAppSelector(selectIsUpdating)
  const buttonDestination = useAppSelector(selectButtonDestination)

  const { t } = useTranslation()

  const iconImageUrl = ImageUtils.createUrlFromImage(purchaseOrder?.vendor?.iconImage, { heightPx: 200, widthPx: 200 })

  return <Container className="h-full w-full bg-fg-gray-surface-light border-l border-fg-gray-surface">
    <div className="flex items-center space-x-4">
      <div>
        {
          (() => {
            if (isLoading) {
              return <div className={`h-12 w-12`}>
                <Skeleton circle width="100%" height="100%" />
              </div>
            } else {
              return <div>
                {iconImageUrl ?
                  <Image
                    src={iconImageUrl}
                    alt="Company Logo"
                    objectFit="cover"
                    sizeClass="h-12 w-12 ring-fg-gray-surface ring-1 rounded-full"
                    className="rounded-full bg-fg-gray-surface"
                  /> : <div className="h-12 w-12 ring-fg-gray-surface ring-1 rounded-full bg-fg-gray-surface" />
                }
              </div>
            }
          })()
        }
      </div>
      <div className="flex flex-col space-y-2">
        <Paragraph className="text-fg-gray-dark">
          {isLoading ? <Skeleton className="!w-24"/> : t('checkout:checkout_row_title_ordering_from')}
        </Paragraph>
        <Body className="">
          {isLoading ? <Skeleton className="!w-64"/> : purchaseOrder?.vendor?.name}
        </Body>
      </div>
    </div>

    <div className="my-8">
      <Button
        isFullWidth={true}
        isLoading={isPurchasing || isUpdating}
        isSkeletonLoading={isLoading}
        onClick={onButtonClick}
        buttonStyle={buttonDestination ? 'secondary' : 'primary'}
        size="lg"
      >
        <LocalizedTextLabel t={t} text={buttonTitle}/>
      </Button>
    </div>

    <div className="flex">
      <Paragraph className="text-left">{isLoading || isUpdating ? <Skeleton className="!w-16" /> : t('checkout:checkout_row_title_subtotal')}</Paragraph>
      <Paragraph className="text-right flex-grow">{isLoading || isUpdating ? <Skeleton className="!w-16" /> : CurrencyUtils.stringFromPrice(purchaseOrder?.paymentSummary?.subtotal, purchaseOrder?.paymentSummary?.currencyCode)}</Paragraph>
    </div>

    <div className="flex mt-6">
      <Paragraph className="text-left">{isLoading || isUpdating ? <Skeleton className="!w-16" /> : t('checkout:checkout_row_title_shipping')}</Paragraph>
      <Paragraph className="text-right flex-grow">{isLoading || isUpdating ? <Skeleton className="!w-16" /> : CurrencyUtils.stringFromPrice(purchaseOrder?.paymentSummary?.shipping, purchaseOrder?.paymentSummary?.currencyCode)}</Paragraph>
    </div>

    <div className={`flex mt-6 ${purchaseOrder?.paymentSummary?.discount ? '' : 'hidden'}`}>
      <Paragraph className="text-left text-fg-primary">{isLoading || isUpdating ? <Skeleton className="!w-16" /> : t('checkout:checkout_row_title_discount')}</Paragraph>
      <Paragraph className="text-right text-fg-primary flex-grow">-{isLoading ? <Skeleton className="!w-16" /> : CurrencyUtils.stringFromPrice(purchaseOrder?.paymentSummary?.discount, purchaseOrder?.paymentSummary?.currencyCode)}</Paragraph>
    </div>

    <div className="flex mt-6">
      <Paragraph className="text-left">{isLoading || isUpdating ? <Skeleton className="!w-16" /> : t('checkout:checkout_row_title_tax')}</Paragraph>
      <Paragraph className="text-right flex-grow">{isLoading || isUpdating ? <Skeleton className="!w-16" /> : CurrencyUtils.stringFromPrice(purchaseOrder?.paymentSummary?.tax, purchaseOrder?.paymentSummary?.currencyCode)}</Paragraph>
    </div>

    <div className="mt-6 border-b-2 border-fg-gray-surface-dark" />

    <div className="flex mt-6">
      <Heading3 className="text-left">{isLoading || isUpdating ? <Skeleton className="!w-16" /> : t('checkout:checkout_row_title_total')}</Heading3>
      <Heading3 className="text-right flex-grow">{isLoading || isUpdating ? <Skeleton className="!w-16" /> : CurrencyUtils.stringFromPrice(purchaseOrder?.paymentSummary?.getTotal(), purchaseOrder?.paymentSummary?.currencyCode)}</Heading3>
    </div>
  </Container>
}